import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PORTAL, usePortal } from '../portalAuth';
import { IMG, useSite, PaymentIcons } from '../components/StoreLayout';
import SITE from '../api';
import { openRazorpayCheckout } from '../razorpay';
import { trackEvent } from '../analytics';
import { STORE_BASE } from '../routeBase';

const INDIA_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 'Haryana',
  'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana',
  'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Andaman and Nicobar Islands', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

export default function Cart() {
  const { customer, cart, setCart, ready } = usePortal();
  const { content: c, razorpayConfigured } = useSite();
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [couponErr, setCouponErr] = useState('');
  const [couponBusy, setCouponBusy] = useState(false);
  const [note, setNote] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [countries, setCountries] = useState([]);

  // delivery address — pre-filled from the customer's saved profile, editable here
  const [addr, setAddr] = useState({ address: '', city: '', state: '', postal_code: '', country_id: '', gstin: '' });
  const [saveToProfile, setSaveToProfile] = useState(true);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState(null);

  // courier shipping estimate
  const [couriers, setCouriers] = useState([]);
  const [selectedCourier, setSelectedCourier] = useState('');
  const [estResult, setEstResult] = useState(null);
  const [estBusy, setEstBusy] = useState(false);
  const [estErr, setEstErr] = useState('');

  // "Request payment link" confirm-conversion dialog state — declared here
  // with every other hook (not after the early returns below) so hook order
  // never changes between renders, per the Rules of Hooks.
  const [plPreview, setPlPreview] = useState(null);
  const [plLoading, setPlLoading] = useState(false);

  useEffect(() => { SITE.get('/countries').then(r => setCountries(r.data.countries || [])); }, []);
  useEffect(() => {
    if (!customer) return;
    setAddr({
      address: customer.address || '', city: customer.city || '', state: customer.state || '',
      postal_code: customer.postal_code || '', country_id: customer.country_id || '', gstin: customer.gstin || '',
    });
    PORTAL.get('/couriers').then(r => setCouriers(r.data.couriers || [])).catch(() => {});
    PORTAL.get('/addresses').then(r => {
      const list = r.data.addresses || [];
      setSavedAddresses(list);
      const def = list.find(a => a.is_default);
      if (def) applySavedAddress(def);
    }).catch(() => {});
  }, [customer]);

  const applySavedAddress = a => {
    setSelectedAddrId(a.id);
    setAddr({
      address: a.address_line || '', city: a.city || '', state: a.state || '',
      postal_code: a.pincode || '', country_id: a.country_id || '', gstin: addr.gstin,
    });
  };

  if (!ready) return <div style={{ padding: 60, textAlign: 'center', color: '#aaa' }}>Loading…</div>;
  if (!customer) return (
    <div style={{ maxWidth: 500, margin: '60px auto', textAlign: 'center', padding: 20 }}>
      <i className="ti ti-shopping-cart" style={{ fontSize: 44, color: '#ddd' }} />
      <h2 style={{ marginTop: 12 }}>Please sign in to view your cart</h2>
      <Link to={`${STORE_BASE}/login`} className={btnClass()} style={btn()}>Sign in</Link>
    </div>
  );

  const setQty = async (variantId, qty) => {
    const r = await PORTAL.put(`/cart/${variantId}`, { qty });
    setCart(r.data);
  };
  const remove = async variantId => { const r = await PORTAL.delete(`/cart/${variantId}`); setCart(r.data); };

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponBusy(true); setCouponErr('');
    try { const r = await PORTAL.post('/cart/coupon', { code: couponInput.trim() }); setCart(r.data); setCouponInput(''); }
    catch (e) { setCouponErr(e.response?.data?.error || 'Could not apply coupon'); }
    setCouponBusy(false);
  };
  const removeCoupon = async () => { const r = await PORTAL.delete('/cart/coupon'); setCart(r.data); };

  const isIndiaSelected = (countryId) => {
    const country = countries.find(co => co.id === Number(countryId));
    return country && (country.code === 'IN' || /^india$/i.test(country.name));
  };

  const calculateShipping = async (courierId) => {
    if (!courierId) { setEstResult(null); setEstErr(''); return; }
    setEstBusy(true); setEstErr(''); setEstResult(null);
    try {
      const r = await PORTAL.post('/estimate-courier-shipping', { courier_id: courierId });
      setEstResult(r.data);
    } catch (e) { setEstErr(e.response?.data?.error || 'Could not calculate shipping'); }
    setEstBusy(false);
  };

  // Item 5 (Batch F) — postal/ZIP code is deliberately NOT required: a real
  // share of the countries this site ships to don't use postal codes at
  // all, so treating it as mandatory blocked otherwise-complete, legitimate
  // orders from those customers. The field still accepts alphanumeric input
  // (several countries' postal codes are alphanumeric, e.g. the UK, Canada)
  // — nothing here or on the backend restricts it to digits.
  const addressComplete = addr.address.trim() && addr.city.trim() && addr.country_id;
  const canCheckout = agreeTerms && addressComplete;

  // Drives the "here's what's missing" message near the payment buttons —
  // the Delivery Address section above is what's actually required; the
  // Estimate Shipping and Tax section below it is optional and never blocks
  // checkout, but the two look similar enough that it's an easy mix-up.
  const missingReasons = [];
  if (!addr.address.trim()) missingReasons.push('street address');
  if (!addr.city.trim()) missingReasons.push('city');
  if (!addr.country_id) missingReasons.push('country');
  if (!agreeTerms) missingReasons.push('agreeing to the terms and conditions');

  // Opens the Razorpay Checkout.js modal for a freshly-placed order. The
  // "Order placed" screen only appears once the modal is resolved one way or
  // another (paid, dismissed, or failed) — never while it's still open —
  // so the customer's very next thing they see after clicking Pay Now is the
  // actual payment form, matching how the old site's flow worked.
  // Same items/value shape for both purchase points below (a Razorpay-
  // verified payment resolves asynchronously, after the cart's already
  // been cleared, so the snapshot has to be captured up front rather than
  // read off `cart` at the moment either one actually fires).
  const trackPurchase = (orderPayload, cartSnapshot) => trackEvent('purchase', {
    transaction_id: orderPayload.order_number || orderPayload.order_id,
    value: cartSnapshot.net_total ?? cartSnapshot.total, currency: cartSnapshot.currency || 'USD',
    items: cartSnapshot.items.map(it => ({ item_id: it.model_no, item_variant: it.color, quantity: it.qty, price: it.unit_price })),
  });

  const openRazorpay = (orderPayload, cartSnapshot) => openRazorpayCheckout(orderPayload.order_id, orderPayload.razorpay, {
    onVerified: (payment_status) => { setPlaced({ ...orderPayload, payment_status, verified: true }); trackPurchase(orderPayload, cartSnapshot); },
    onPending: (reason) => setPlaced({ ...orderPayload, payment_status: 'pending', [reason]: true }),
  });

  const checkout = async (payment_choice, extra = {}) => {
    if (!addressComplete) { alert('Please complete your delivery address before checking out.'); return; }
    if (!agreeTerms) { alert('Please agree to the terms and conditions to place your order.'); return; }
    setPlacing(true);
    const cartSnapshot = cart; // captured before setCart() clears it below
    trackEvent('begin_checkout', {
      value: cartSnapshot.net_total ?? cartSnapshot.total, currency: cartSnapshot.currency || 'USD',
      items: cartSnapshot.items.map(it => ({ item_id: it.model_no, item_variant: it.color, quantity: it.qty, price: it.unit_price })),
    });
    try {
      if (saveToProfile) await PORTAL.put('/me', addr).catch(() => {});
      const country = countries.find(co => co.id === Number(addr.country_id));
      const r = await PORTAL.post('/order', {
        payment_choice, notes: note, agree_terms: true,
        ship_address: addr.address, ship_city: addr.city, ship_state: addr.state,
        ship_postal_code: addr.postal_code, ship_country: country?.name || '', ship_gstin: addr.gstin,
        ...extra,
      });
      setCart({ items: [], count: 0, total: 0 });
      if (payment_choice === 'pay_now' && r.data.razorpay) {
        setPlacing(false);
        await openRazorpay(r.data, cartSnapshot);
        return;
      }
      setPlaced(r.data);
      // Anything that didn't hand off to openRazorpay above (on_account,
      // payment_link, or pay_now with no Razorpay gateway configured) has
      // no separate payment-verification step — placing the order itself
      // is the purchase moment.
      trackPurchase(r.data, cartSnapshot);
    } catch (e) { alert(e.response?.data?.error || 'Could not place order'); }
    setPlacing(false);
  };

  // Both online-payment routes (Pay Now and Request payment link) preview
  // first — India customers and customers already priced in USD go straight
  // through (no conversion to show); anyone else must see and confirm the
  // live USD conversion before the order gets created, whichever button
  // they clicked.
  const startOnlinePayment = async (payment_choice) => {
    if (!addressComplete) { alert('Please complete your delivery address before checking out.'); return; }
    if (!agreeTerms) { alert('Please agree to the terms and conditions to place your order.'); return; }
    setPlLoading(true);
    try {
      const r = await PORTAL.get('/payment-link/preview');
      if (r.data.needs_confirmation) { setPlPreview({ ...r.data, payment_choice }); setPlLoading(false); return; }
      setPlLoading(false);
      await checkout(payment_choice);
    } catch (e) { setPlLoading(false); alert(e.response?.data?.error || 'Could not start checkout'); }
  };

  const confirmOnlinePayment = async () => {
    const preview = plPreview;
    setPlPreview(null);
    await checkout(preview.payment_choice, { confirmed_usd_amount: preview.amount, confirmed_rate: preview.rate });
  };

  if (placed) return (
    <div style={{ maxWidth: 560, margin: '50px auto', padding: 20 }}>
      <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 32, textAlign: 'center' }}>
        <i className="ti ti-circle-check" style={{ fontSize: 52, color: placed.verified ? '#16a34a' : '#0d6efd' }} />
        <h1 style={{ fontSize: 24, marginTop: 10 }}>{placed.verified ? 'Payment successful' : 'Order placed'}</h1>
        <p style={{ color: '#666', margin: '8px 0 4px' }}>Order <strong>{placed.order_number}</strong></p>
        <p style={{ color: '#666', fontSize: 14 }}>Total: {placed.currency} {Number(placed.total).toLocaleString()}</p>
        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 12, margin: '16px 0', fontSize: 13, color: '#555' }}>
          {placed.payment_choice === 'on_account' && 'Placed on account — you can settle payment later.'}
          {placed.payment_choice === 'pay_now' && placed.verified && 'Your payment was received and verified — thank you!'}
          {placed.payment_choice === 'pay_now' && !placed.verified && !placed.razorpay && 'Payment via Razorpay will be enabled once your gateway is connected — your order is placed and can be settled later.'}
          {placed.payment_choice === 'pay_now' && !placed.verified && placed.razorpay && placed.dismissed && "You closed the payment window before finishing. Your order is placed — pay any time from My Orders."}
          {placed.payment_choice === 'pay_now' && !placed.verified && placed.razorpay && placed.payment_failed && 'Your payment did not go through. Your order is placed — you can try paying again from My Orders.'}
          {placed.payment_choice === 'pay_now' && !placed.verified && placed.razorpay && placed.verify_error && "We couldn't confirm that payment automatically. If money was deducted, it will reflect shortly — otherwise please try again from My Orders."}
          {placed.payment_choice === 'pay_now' && !placed.verified && placed.razorpay && placed.script_load_failed && 'The payment window could not load. Your order is placed — please try paying from My Orders.'}
          {placed.payment_choice === 'payment_link' && placed.razorpay?.payment_link_url && (
            <>
              We've generated a secure payment link for this order.
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                <a href={placed.razorpay.payment_link_url} target="_blank" rel="noreferrer" className={btnClass()} style={btn()}>Open Payment Link</a>
                <button onClick={() => { navigator.clipboard?.writeText(placed.razorpay.payment_link_url); }} className="sf-btn-outline" style={{ padding: '9px 16px', borderRadius: 8, border: '1.5px solid var(--sf-accent)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Copy Link</button>
              </div>
            </>
          )}
          {placed.payment_choice === 'payment_link' && !placed.razorpay?.payment_link_url && 'We will send you a payment link to complete the card payment once your gateway is connected.'}
        </div>
        <Link to={`${STORE_BASE}/orders`} className={btnClass()} style={btn()}>View my orders</Link>
        <Link to={`${STORE_BASE}/shop`} className={btnClass()} style={{ ...btn(), marginLeft: 10 }}>Continue shopping</Link>
      </div>
    </div>
  );

  if (!cart.items.length) return (
    <div style={{ maxWidth: 500, margin: '60px auto', textAlign: 'center', padding: 20 }}>
      <i className="ti ti-shopping-cart" style={{ fontSize: 44, color: '#ddd' }} />
      <h2 style={{ marginTop: 12 }}>Your cart is empty</h2>
      <Link to={`${STORE_BASE}/shop`} className={btnClass()} style={btn()}>Browse products</Link>
    </div>
  );

  return (
    <div style={{ maxWidth: 1100, margin: '24px auto', padding: '0 24px', display: 'flex', gap: 26, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 620px' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 16 }}>Shopping Cart ({cart.count})</h1>

        {/* header row */}
        <div style={{ display: 'flex', background: 'var(--sf-body-bg, #f5f5f5)', padding: '10px 12px', borderRadius: '10px 10px 0 0', fontSize: 11.5, fontWeight: 700, letterSpacing: 0.6, color: '#555', textTransform: 'uppercase' }}>
          <div style={{ flex: '0 0 70px' }} />
          <div style={{ flex: 2 }}>Product</div>
          <div style={{ flex: 1, textAlign: 'center' }}>Price</div>
          <div style={{ flex: 1, textAlign: 'center' }}>Quantity</div>
          <div style={{ flex: 1, textAlign: 'right' }}>Total</div>
          <div style={{ flex: '0 0 30px' }} />
        </div>

        {cart.items.map(it => (
          <div key={it.variant_id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #eee', borderTop: 'none', padding: 12 }}>
            <div style={{ flex: '0 0 70px', width: 70, height: 70, background: '#fafafa', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {it.image ? <img src={IMG(it.image)} alt="" loading="lazy" style={{ maxWidth: '90%', maxHeight: '90%' }} /> : <i className="ti ti-eyeglass-2" style={{ color: '#ddd', fontSize: 26 }} />}
            </div>
            <div style={{ flex: 2 }}>
              <div style={{ fontWeight: 600 }}>{it.model_no}</div>
              <div style={{ fontSize: 12.5, color: '#888' }}>Color: {it.color} · Size: {it.size}mm</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center', fontSize: 13.5 }}>{it.unit_price != null ? `${cart.currency} ${it.unit_price}` : '—'}</div>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, border: '1px solid #ddd', borderRadius: 8, padding: '2px 4px' }}>
                <button onClick={() => setQty(it.variant_id, it.qty - 1)} style={qtyBtn}>−</button>
                <input type="number" min="1" value={it.qty}
                  onChange={e => { const n = Math.max(1, parseInt(e.target.value, 10) || 1); setQty(it.variant_id, n); }}
                  style={{ width: 42, textAlign: 'center', border: 'none', fontSize: 13.5 }} />
                <button onClick={() => setQty(it.variant_id, it.qty + 1)} style={qtyBtn}>+</button>
              </div>
            </div>
            <div style={{ flex: 1, textAlign: 'right', fontWeight: 600 }}>{it.line_total != null ? `${cart.currency} ${Number(it.line_total).toLocaleString()}` : '—'}</div>
            <div style={{ flex: '0 0 30px', textAlign: 'center' }}>
              <button onClick={() => remove(it.variant_id)} style={{ border: 'none', background: 'none', color: '#c0392b', cursor: 'pointer' }}><i className="ti ti-x" /></button>
            </div>
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 4px' }}>
          <Link to={`${STORE_BASE}/shop`} style={{ fontSize: 13.5, color: 'var(--sf-accent)', textDecoration: 'none' }}><i className="ti ti-chevron-left" /> Continue shopping</Link>
        </div>
        <p style={{ fontSize: 12, color: '#999' }}>All orders are processed in {cart.currency}. Prices shown reflect your account's wholesale pricing tier.</p>

        {/* delivery address */}
        <div style={sectionCard}>
          <h3 style={sectionTitle}>Delivery Address</h3>
          {savedAddresses.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
              {savedAddresses.map(a => (
                <button key={a.id} onClick={() => applySavedAddress(a)} style={{
                  textAlign: 'left', padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                  border: selectedAddrId === a.id ? '1.5px solid var(--sf-accent)' : '1.5px solid #e5e5e5',
                  background: selectedAddrId === a.id ? 'rgba(0,0,0,0.03)' : '#fff', maxWidth: 220,
                }}>
                  <strong>{a.label}</strong>{a.is_default ? ' (default)' : ''}
                  <div style={{ color: '#777', marginTop: 2 }}>{a.address_line}, {a.city}</div>
                </button>
              ))}
              <button onClick={() => setSelectedAddrId(null)} style={{
                padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, alignSelf: 'center',
                border: selectedAddrId === null ? '1.5px solid var(--sf-accent)' : '1.5px solid #e5e5e5',
                background: selectedAddrId === null ? 'rgba(0,0,0,0.03)' : '#fff',
              }}>
                + Use a different address
              </button>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <input style={{ ...inp, gridColumn: '1 / -1' }} placeholder="Address *" value={addr.address} onChange={e => setAddr(a => ({ ...a, address: e.target.value }))} />
            <input style={inp} placeholder="City *" value={addr.city} onChange={e => setAddr(a => ({ ...a, city: e.target.value }))} />
            {isIndiaSelected(addr.country_id) ? (
              <select style={inp} value={addr.state} onChange={e => setAddr(a => ({ ...a, state: e.target.value }))}>
                <option value="">State</option>
                {INDIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : (
              <input style={inp} placeholder="State / Province" value={addr.state} onChange={e => setAddr(a => ({ ...a, state: e.target.value }))} />
            )}
            <div>
              <input style={inp} placeholder="Postal / ZIP Code" value={addr.postal_code} onChange={e => setAddr(a => ({ ...a, postal_code: e.target.value }))} />
              <div style={{ fontSize: 11, color: '#999', marginTop: 3 }}>Not required. If your country doesn't use a postal code, just type 00000.</div>
            </div>
            <select style={inp} value={addr.country_id} onChange={e => setAddr(a => ({ ...a, country_id: e.target.value }))}>
              <option value="">Country *</option>
              {countries.map(co => <option key={co.id} value={co.id}>{co.name}</option>)}
            </select>
            {isIndiaSelected(addr.country_id) && (
              <input style={{ ...inp, gridColumn: '1 / -1' }} placeholder="GSTIN (optional)" value={addr.gstin} onChange={e => setAddr(a => ({ ...a, gstin: e.target.value }))} />
            )}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 10, fontSize: 12.5, color: '#666', cursor: 'pointer' }}>
            <input type="checkbox" checked={saveToProfile} onChange={e => setSaveToProfile(e.target.checked)} />
            Save this address to my profile for next time
          </label>
        </div>

        {/* estimate shipping via courier */}
        <div style={sectionCard}>
          <h3 style={sectionTitle}>Estimate Shipping</h3>
          <p style={{ fontSize: 11.5, color: '#999', margin: '-6px 0 12px' }}>
            Optional — pick a courier to see an estimated shipping cost before you order, based on {cart.count} piece{cart.count === 1 ? '' : 's'} in your cart. This never blocks checkout.
          </p>
          {couriers.length === 0 ? (
            <div style={{ fontSize: 12.5, color: '#999' }}>
              No couriers are set up for your country yet — shipping will be arranged after your order is confirmed.
            </div>
          ) : (
            <>
              <select style={inp} value={selectedCourier} onChange={e => {
                const v = e.target.value;
                setSelectedCourier(v);
                calculateShipping(v);
              }}>
                <option value="">Select a courier…</option>
                {couriers.map(co => <option key={co.id} value={co.id}>{co.name}</option>)}
              </select>
              {estBusy && <div style={{ fontSize: 12.5, color: '#999', marginTop: 8 }}>Calculating…</div>}
              {estErr && <div style={{ color: '#c0392b', fontSize: 12.5, marginTop: 8 }}>{estErr}</div>}
              {estResult && (
                <div style={{ marginTop: 12, fontSize: 13.5, color: '#444' }}>
                  {estResult.weight_breakdown ? (
                    <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '8px 10px', marginBottom: 8, fontSize: 12.5 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Gross weight</span><span>{estResult.weight_breakdown.grossKg} kg</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Volumetric weight</span><span>{estResult.weight_breakdown.volumetricKg} kg</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, borderTop: '1px solid #e5e7eb', marginTop: 4, paddingTop: 4 }}>
                        <span>Chargeable weight ({estResult.weight_breakdown.weightSource === 'volumetric' ? 'volumetric' : 'gross'} is higher)</span>
                        <span>{estResult.weight_breakdown.chargeableKg} kg</span>
                      </div>
                    </div>
                  ) : (
                    <div>Estimated weight: <strong>{estResult.weight_kg} kg</strong></div>
                  )}
                  <div>Shipping via {estResult.courier_name}: <strong>{estResult.display.currency} {Number(estResult.display.amount).toLocaleString()}</strong></div>
                  {estResult.display.converted && (
                    <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>Converted from ₹{Number(estResult.total_inr).toLocaleString()} at today's rate</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* sidebar */}
      <div style={{ flex: '1 1 300px', maxWidth: 340, position: 'sticky', top: 16 }}>
        <div style={sectionCard}>
          <h3 style={sectionTitle}>Add a Note to Your Order</h3>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={4}
            placeholder="Any special instructions for this order…"
            style={{ ...inp, width: '100%', resize: 'vertical', boxSizing: 'border-box' }} />
        </div>

        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 14 }}>Order Summary</h3>
          <Row label={`Items (${cart.count})`} value={cart.price_visible ? `${cart.currency} ${Number(cart.total).toLocaleString()}` : '—'} />

          {cart.price_visible && cart.applied_schemes?.length > 0 && cart.applied_schemes.map(s => (
            <Row key={s.id} label={<span style={{ color: '#16a34a' }}><i className="ti ti-discount-2" /> {s.name}</span>}
              value={<span style={{ color: '#16a34a' }}>-{cart.currency} {Number(s.discount).toLocaleString()}</span>} />
          ))}
          {cart.price_visible && cart.applied_coupon && (
            <Row label={<span style={{ color: '#16a34a' }}><i className="ti ti-ticket" /> Coupon {cart.applied_coupon.code}</span>}
              value={<span style={{ color: '#16a34a' }}>-{cart.currency} {Number(cart.applied_coupon.discount).toLocaleString()}</span>} />
          )}

          {estResult ? (
            <>
              <Row label={`Shipping via ${estResult.courier_name}`} value={`${estResult.display.currency} ${Number(estResult.display.amount).toLocaleString()}`} />
              {estResult.gst_pct > 0 && <Row label={`incl. GST (${estResult.gst_pct}%)`} value={`${estResult.display.currency} ${Number(estResult.display.amount * estResult.gst_amount / estResult.total_inr).toFixed(2)}`} />}
            </>
          ) : (
            <Row label="Shipping &amp; taxes" value={<i style={{ color: '#999' }}>Calculated at checkout</i>} />
          )}

          <div style={{ borderTop: '1px solid #eee', margin: '12px 0', paddingTop: 12, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 17 }}>
            <span>Total</span>
            <span>
              {cart.price_visible
                ? `${cart.currency} ${Number((cart.net_total ?? cart.total) + (estResult && estResult.display.currency === cart.currency ? estResult.display.amount : 0)).toLocaleString()}`
                : 'On request'}
            </span>
          </div>

          {cart.price_visible && (
            <div style={{ margin: '12px 0' }}>
              {cart.applied_coupon ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}>
                  <span>Coupon <strong>{cart.applied_coupon.code}</strong> applied</span>
                  <button onClick={removeCoupon} style={{ border: 'none', background: 'none', color: '#c0392b', fontSize: 12, cursor: 'pointer' }}>Remove</button>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input value={couponInput} onChange={e => setCouponInput(e.target.value)} placeholder="Coupon code"
                      style={{ flex: 1, padding: '8px 10px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 13, fontFamily: 'monospace', textTransform: 'uppercase' }} />
                    <button onClick={applyCoupon} disabled={couponBusy} className="sf-btn-outline" style={{ padding: '8px 14px', borderRadius: 8, border: '1.5px solid var(--sf-accent)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      {couponBusy ? '…' : 'Apply'}
                    </button>
                  </div>
                  {couponErr && <div style={{ color: '#c0392b', fontSize: 12, marginTop: 6 }}>{couponErr}</div>}
                </div>
              )}
            </div>
          )}

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '14px 0', fontSize: 12.5, color: '#555', cursor: 'pointer' }}>
            <input type="checkbox" checked={agreeTerms} onChange={e => setAgreeTerms(e.target.checked)} style={{ marginTop: 2 }} />
            <span>I agree with the <Link to={`${STORE_BASE}/page/terms-and-conditions`} target="_blank" style={{ color: 'var(--sf-accent)' }}>terms and conditions</Link></span>
          </label>

          <div style={{ fontSize: 12.5, color: '#888', margin: '6px 0 8px', fontWeight: 600 }}>CHOOSE PAYMENT</div>
          {missingReasons.length > 0 && (
            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '9px 12px', fontSize: 12, color: '#9a3412', marginBottom: 10 }}>
              Before you can check out, please complete: <strong>{missingReasons.join(', ')}</strong> above.
            </div>
          )}
          <button onClick={() => startOnlinePayment('pay_now')} disabled={placing || plLoading || !canCheckout} className="sf-btn-outline" style={payBtn(!canCheckout)}>
            <i className="ti ti-credit-card" /> {plLoading ? 'Checking…' : 'Pay now (Razorpay)'}
          </button>
          <button onClick={() => startOnlinePayment('payment_link')} disabled={placing || plLoading || !canCheckout} className="sf-btn-outline" style={payBtn(!canCheckout)}>
            <i className="ti ti-link" /> {plLoading ? 'Checking…' : 'Request payment link'}
          </button>
          <button onClick={() => checkout('on_account')} disabled={placing || !canCheckout} className="sf-btn-outline" style={payBtn(!canCheckout)}>
            <i className="ti ti-notebook" /> Place order, pay later
          </button>
          {!razorpayConfigured && (
            <p style={{ fontSize: 11.5, color: '#aaa', marginTop: 6, textAlign: 'center' }}>
              Razorpay online payment activates once your gateway keys are added.
            </p>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: 18, margin: '16px 0 10px', flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#777', fontWeight: 600, textTransform: 'uppercase' }}>
              <i className="ti ti-lock" style={{ fontSize: 16, color: 'var(--sf-accent)' }} /> Secure SSL Encryption
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#777', fontWeight: 600, textTransform: 'uppercase' }}>
              <i className="ti ti-shield-check" style={{ fontSize: 16, color: 'var(--sf-accent)' }} /> Guaranteed Safe Checkout
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <PaymentIcons names={c['footer.payment_methods']} />
          </div>
        </div>
      </div>

      {plPreview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}
          onClick={e => e.target === e.currentTarget && setPlPreview(null)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: '100%', maxWidth: 420, textAlign: 'center' }}>
            <i className="ti ti-currency-dollar" style={{ fontSize: 40, color: 'var(--sf-accent)' }} />
            <h2 style={{ fontSize: 18, marginTop: 8 }}>Confirm USD Amount</h2>
            <p style={{ color: '#666', fontSize: 13.5, margin: '10px 0' }}>
              Your order total is <strong>{plPreview.original_currency} {Number(plPreview.original_amount).toLocaleString()}</strong>.
              {plPreview.payment_choice === 'pay_now' ? ' You will be charged ' : ' Your payment link will be for '}
              <strong style={{ color: 'var(--sf-accent)', fontSize: 16 }}>USD {Number(plPreview.amount).toLocaleString()}</strong> at
              today's rate (1 USD = {plPreview.original_currency} {Number(plPreview.rate).toLocaleString(undefined, { maximumFractionDigits: 4 })}).
            </p>
            <p style={{ color: '#999', fontSize: 11.5, marginBottom: 20 }}>Rate fetched just now — it may vary slightly by the time you complete payment.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => setPlPreview(null)} style={{ padding: '9px 18px', borderRadius: 8, border: '1.5px solid #ddd', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={confirmOnlinePayment} disabled={placing} className={btnClass()} style={btn()}>
                {placing ? 'Please wait…' : `Continue with USD ${Number(plPreview.amount).toLocaleString()}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#555', marginBottom: 6 }}>
    <span>{label}</span><span>{value}</span>
  </div>
);
const btn = () => ({ display: 'inline-block', marginTop: 18, padding: '10px 24px', borderRadius: 22,
  border: '1.5px solid var(--sf-accent)', textDecoration: 'none', fontSize: 14, fontWeight: 600 });
const btnClass = () => 'sf-btn-outline';
const qtyBtn = { width: 24, height: 24, border: 'none', background: 'none', cursor: 'pointer', fontSize: 15 };
const payBtn = (disabled) => ({ width: '100%', padding: '11px', marginBottom: 8, borderRadius: 8,
  border: '1.5px solid var(--sf-accent)', fontSize: 13.5, fontWeight: 600,
  cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 });
const sectionCard = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 18, marginTop: 16 };
const sectionTitle = { fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: '#555', marginBottom: 12 };
const inp = { padding: '9px 11px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 13.5, fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' };
